import { useCallback, useEffect, type ReactNode } from 'react'
import { useHasLiveAudioNote } from '@renderer/hooks/audio/use-audio-note-selectors'
import { useIsLoggedIn } from '@renderer/hooks/useIsLoggedIn'
import { ScrollButton } from '../../view-scroll-button'
import { viewChrome } from '../../../view-registry'
import { useElementHeightObserver } from '../../../hooks/use-element-height-observer'
import { useFloatingBarScrollSlot, useSetSidepanelFloatingBarHeight } from '../../../atoms'
import { HOME_VIEW_ID, useViewNavigation } from '../../../view-atoms'
import { SIDEPANEL_LAYER_Z } from '../../../constants'
import { SidepanelMeetingCluster } from './meeting-cluster'
import { View } from '../../view'
import { publishedFloatingBarHeight } from './floating-bar-height'
import { ContextNotificationCard, ContextNotificationPill, useContextNotification } from '../../context-notification'

// Floating controls row above the chat input: the live meeting's controls and
// the jump-to-latest slot on the left (the latter registered by the active
// view's scrollable body), and on the right whatever controls the open view
// declares in the registry.
export function SidepanelFloatingBar({
  showsMeetingCluster = true,
  status,
}: {
  /** Off for a View hosted beside the panel, which already carries the live meeting. */
  showsMeetingCluster?: boolean
  /** A passing status, floated over the row without reserving room in the view body. */
  status?: ReactNode
}) {
  const isLoggedIn = useIsLoggedIn()
  const scrollSlot = useFloatingBarScrollSlot()
  const { topView } = useViewNavigation()
  const setFloatingBarHeight = useSetSidepanelFloatingBarHeight()
  // Per-field selector, not useAudioNoteState(): that whole-object hook is
  // rebroadcast per transcript tick, and this row only needs a boolean.
  const hasActiveNote = useHasLiveAudioNote()

  // Registry-driven rather than a chain of id checks: a view that wants
  // controls declares them next to its own definition, so this row never grows
  // a branch per view. `stretch` claims everything left of the meeting cluster
  // and the scroll button; `compact` sits at its natural width, right-aligned.
  const floatingBar = viewChrome(topView)?.floatingBar
  const slot = floatingBar?.render({ id: topView.id, context: topView.context })
  const floatsScrollAboveSlot = !!slot && floatingBar?.scrollButtonPlacement === 'above'

  // The meeting cluster leads the row wherever the open view hasn't claimed it
  // (incl. Home) while a note is live. When idle nothing shows — starting a
  // note lives in the Upcoming quick-action row and the hamburger menu. The
  // Meeting and Action Views own the pill themselves, so there's one cluster.
  const showMeetingCluster = showsMeetingCluster && hasActiveNote && !floatingBar?.ownsAudioPill
  const stacksMeetingCluster = showMeetingCluster && !!slot && floatingBar?.meetingClusterPlacement === 'below'
  // Optional-chained rather than gated on the slot: the button outlives it,
  // still on screen animating out after the view that registered it has gone,
  // and a press during that exit has nothing left to scroll.
  const handleScrollToLatest = useCallback(() => scrollSlot?.onClick(), [scrollSlot])
  // Only persistent chrome (the meeting cluster and view-declared controls)
  // reserves room in the view body's bottom inset. The jump-to-latest button
  // is transient — if it counted, its appearance would grow the published
  // height and re-pad (visibly shift) the message list it floats over.
  const reservesRoom = showMeetingCluster || !!slot

  // The post-onboarding context notification is Home-only: drill-in views,
  // their drawers and popout windows (whose history is rooted on the popped
  // view, so `canGoBack` can't stand in for this) never show it, and it comes
  // back when the user returns.
  const notification = useContextNotification()
  const isHome = topView.id === HOME_VIEW_ID

  const publishHeight = useCallback(
    (height: number) => {
      setFloatingBarHeight(publishedFloatingBarHeight(height, reservesRoom))
    },
    [setFloatingBarHeight, reservesRoom],
  )
  const barRef = useElementHeightObserver(publishHeight)

  useEffect(() => {
    if (!isLoggedIn) {
      setFloatingBarHeight(0)
    }
    return () => setFloatingBarHeight(0)
  }, [isLoggedIn, setFloatingBarHeight])

  if (!isLoggedIn) return null

  const scrollButton = <ScrollButton show={!!scrollSlot?.show} onClick={handleScrollToLatest} />
  const meetingCluster = showMeetingCluster ? <SidepanelMeetingCluster /> : null
  const notificationPill = <ContextNotificationPill notification={notification} visible={isHome} />
  const slotElement = slot ? (
    <View.Toolbar
      className={
        floatingBar?.layout === 'stretch'
          ? `pointer-events-auto relative flex min-w-0 items-end ${
              stacksMeetingCluster ? 'w-full flex-none' : 'flex-1'
            }`
          : // shrink-0, not flex-1: a fixed set of circles and a pill, and
            // stretching would let it crowd out the controls before it.
            // ml-auto because the row is packed from the leading edge —
            // a compact slot is the only thing that wants the far side.
            'pointer-events-auto relative ml-auto flex shrink-0 items-center gap-2'
      }
    >
      {slot}
    </View.Toolbar>
  ) : null

  return (
    // Anchored above the in-flow composer instead of participating in layout.
    // View bodies reserve scroll affordance for this measured row, but its
    // appearance never shifts the body or composer.
    //
    // The bar owns spacing for the jump button, meeting controls, and view slot.
    //
    // pointer-events-none on the row itself because it spans the panel's full width
    // and is mostly empty, and the content it floats over has to stay
    // clickable through the gaps. Each occupant opts back in.
    <div
      className="pointer-events-none absolute inset-x-0 bottom-full mb-2 flex flex-col gap-2 px-3"
      // Its own place in the panel's paint order rather than the footer's: just
      // above an open View, and *below* that View's bottom sheet, which covers
      // this row rather than letting it float on top of the sheet.
      style={{ zIndex: SIDEPANEL_LAYER_Z.floatingBar }}
    >
      {/* Stacks above the control row so it never competes with the meeting
          cluster or a view's controls. Outside the measured row on purpose: it
          floats over Home as designed, and if it counted toward the published
          height a live meeting would re-pad the body each time it opened or
          closed. */}
      <ContextNotificationCard notification={notification} visible={isHome} />
      {status}
      <div ref={barRef} className={`flex gap-2 pb-1 ${stacksMeetingCluster ? 'flex-col items-stretch' : 'items-end'}`}>
        {/* Jump-to-latest normally leads the row, ahead of the meeting cluster: the
            Meeting and Action Views own the pill themselves, so trailing the
            cluster would leave this button at the row's left edge there and on
            the far side of the pill on Home — the same control in two places
            depending on which view you opened. Leading, it always enters at the
            same edge and everything else shifts over.

            Driven by the slot's `show` rather than the slot's existence, so that
            losing the slot is a state change the button can animate through
            instead of an unmount that cuts it off. */}
        {floatsScrollAboveSlot ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-full mb-2 flex justify-center">
            {scrollButton}
          </div>
        ) : null}
        {stacksMeetingCluster ? (
          <>
            {slotElement}
            <div className="relative z-10 flex items-end gap-2">
              {!floatsScrollAboveSlot && scrollButton}
              {notificationPill}
              {meetingCluster}
            </div>
          </>
        ) : (
          <>
            {!floatsScrollAboveSlot && scrollButton}
            {notificationPill}
            {meetingCluster}
            {slotElement}
          </>
        )}
      </div>
    </div>
  )
}
